<template>
  <div 
    :class="[
      'ecx-alert rounded-lg p-4 flex items-start gap-3',
      variantClasses,
    ]"
    role="alert"
  >
    <!-- Icon -->
    <component 
      v-if="iconComponent"
      :is="iconComponent" 
      :class="['w-5 h-5 flex-shrink-0 mt-0.5', iconColorClasses]"
    />
    <template v-else>
      <!-- Success Icon -->
      <svg v-if="variant === 'success'" :class="['w-5 h-5 flex-shrink-0 mt-0.5', iconColorClasses]" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
      </svg>
      <!-- Error Icon -->
      <svg v-else-if="variant === 'error'" :class="['w-5 h-5 flex-shrink-0 mt-0.5', iconColorClasses]" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
      </svg>
      <!-- Warning Icon -->
      <svg v-else-if="variant === 'warning'" :class="['w-5 h-5 flex-shrink-0 mt-0.5', iconColorClasses]" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
      </svg>
      <!-- Info Icon (default) -->
      <svg v-else :class="['w-5 h-5 flex-shrink-0 mt-0.5', iconColorClasses]" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
      </svg>
    </template>

    <!-- Content -->
    <div class="flex-1">
      <h3 v-if="title" :class="['font-semibold mb-1', textColorClasses]">
        {{ title }}
      </h3>
      <p :class="['text-sm', textColorClasses]">
        <slot>{{ message }}</slot>
      </p>
    </div>

    <!-- Close button -->
    <button
      v-if="dismissible"
      type="button"
      :class="['flex-shrink-0 hover:opacity-70 transition-opacity', iconColorClasses]"
      @click="$emit('dismiss')"
    >
      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue'

export type AlertVariant = 'success' | 'error' | 'warning' | 'info'

export interface AlertProps {
  /** Variante de cor */
  variant?: AlertVariant
  /** Título do alerta */
  title?: string
  /** Mensagem do alerta */
  message?: string
  /** Ícone customizado */
  iconComponent?: Component
  /** Se pode ser fechado */
  dismissible?: boolean
}

const props = withDefaults(defineProps<AlertProps>(), {
  variant: 'info',
  dismissible: false,
})

defineEmits<{
  dismiss: []
}>()

const variantClasses = computed(() => {
  const variants: Record<AlertVariant, string> = {
    success: 'bg-green-50 border border-green-200',
    error: 'bg-red-50 border border-red-200',
    warning: 'bg-yellow-50 border border-yellow-200',
    info: 'bg-blue-50 border border-blue-200',
  }
  return variants[props.variant]
})

const iconColorClasses = computed(() => {
  const colors: Record<AlertVariant, string> = {
    success: 'text-green-600',
    error: 'text-red-600',
    warning: 'text-yellow-600',
    info: 'text-blue-600',
  }
  return colors[props.variant]
})

const textColorClasses = computed(() => {
  const colors: Record<AlertVariant, string> = {
    success: 'text-green-800',
    error: 'text-red-800',
    warning: 'text-yellow-800',
    info: 'text-blue-800',
  }
  return colors[props.variant]
})
</script>

